"use strict";

export default class Autoshrink {

  /**
   * Shrinks the font-size and line-height of the `$child` until its height is lte that of the 
   * `$holderOrHeight`.
   * 
   * It is assumed that the css of the `$child` allows for it to exceed the height of the `$holder`
   * in the first place.
   *
   * Views must ofc already be attached to the DOM, and sized.
   *
   * @param $holderOrHeight can be either a jQuery object or a height value
   * @param $child the "textfield" whose fontSize should be autoshrunk
   * @param fontSize starting fontsize to use (optional)
   * @param lineHeight starting line-height to use (optional)
   */
  static go($holderOrHeight, $child, fontSize=0, lineHeight=0) {

    if (!$holderOrHeight || !$child || !$child[0]) {
      cl ("aborting, bad params");
      return;
    }

    const holderHeight = $holderOrHeight[0] ? $holderOrHeight.height() : $holderOrHeight;
    if (!holderHeight) {
      cl('bad param for $holderOrHeight', $holderOrHeight);
      return;
    }
    if ($child.height() <= holderHeight) {
      cl(`skipping, no need (${$child.height()} vs ${$holderOrHeight})`);
      return;
    }

    // If no explicit fontSize, get computed font size.
    if (!fontSize) {
      let style = window.getComputedStyle($child[0], null).getPropertyValue('font-size');
      fontSize = parseFloat(style);
      if (fontSize) {
        cl('will use computed fontsize - style:', style, 'value:', fontSize);
      }
    }
    if (!fontSize) {
      cl("aborting, no fontsize");
      return;
    }

    // If no explicit lineHeight, get computed line height.
    if (!lineHeight) {
      let style = window.getComputedStyle($child[0], null).getPropertyValue('line-height');
      lineHeight = parseFloat(style);
    }

    let lineHeightRatio = 0;
    if (lineHeight) {
      lineHeightRatio = lineHeight / fontSize;
      // cl('will use explicit line-height using ratio', lineHeightRatio);
    } else {
      // cl('will not set explicit line-height');
      lineHeightRatio = 0;
    }

    Autoshrink.#doBinary($child, holderHeight, fontSize, lineHeightRatio);
  }

  /**
   * Progressively shrinks text until $child is smaller than holderHeight (more straight-forward).
   */
  static #doLinear($child, holderHeight, fontSize, lineHeightRatio=0) {

    const minCredibleFontSize = 6;
    const fontStep = 0.5;
    let passNum = 0;
    const startTime = new Date().getTime();

    while (true) {
      passNum++;
      fontSize -= fontStep;  

      // Apply the css to shrink the font-size (and line-height, if applicable)
      $child.css("font-size", fontSize + "px");
      if (lineHeightRatio) {
        const lineHeight = fontSize * lineHeightRatio;
        $child.css("line-height", lineHeight + "px");
      }

      const height = $child.height();
      if (height < holderHeight) {
        const ms = new Date().getTime() - startTime;
        const delta = holderHeight - height;
        // cl('done, passnum:', passNum, 'height delta', delta, 'ms:', ms, 'fontsize:', fontSize);
        break;
      }
      if (fontSize <= minCredibleFontSize) {  
        clw('Min font size exceeded, giving up');
        break;
      }
    }
  }

  /** 
   * Uses binary search-like logic (more efficient/accurate).
   */
  static #doBinary($child, holderHeight, fontSize, lineHeightRatio=0) {

    // Smallest 'credible' font size we'll allow for.
    const minCredibleFontSize = 6;

    let passNum = 0;
    const startTime = new Date().getTime();

    // Note that if target range is too small, can cause infinite loop 
    // (were it not for max-count failsafe.)
    const targetHeightMin = holderHeight - fontSize;
    const targetHeightMax = holderHeight;
    // cl('height bounds', targetHeightMin, targetHeightMax);

    // Seed the font size bounds.
    let fontSizeUpperBound = fontSize;
    let fontSizeLowerBound = minCredibleFontSize;  

    while (true) {

      passNum++;

      // cl('passnum', passNum, '------');
      // cl('fontsizeupperbound', fontSizeUpperBound);
      // cl('fontsizelowerbound', fontSizeLowerBound);

      fontSize = fontSizeLowerBound + (fontSizeUpperBound - fontSizeLowerBound) / 2;
      // cl('fontsize', fontSize);

      // Apply the css to shrink the font-size (and line-height, if applicable)
      $child.css("font-size", fontSize + "px");
      if (lineHeightRatio) {
        const lineHeight = fontSize * lineHeightRatio;
        $child.css("line-height", lineHeight + "px");
      }
      const height = $child.height();
      // cl('resulting height', height, 'vs', targetHeightMin, targetHeightMax)

      let isTooShort = height < targetHeightMin;
      let isTooTall = height > targetHeightMax;
      
      if (!isTooShort && !isTooTall) {
        const ms = new Date().getTime() - startTime;
        const delta = holderHeight - height;
        // cl('done, passnum:', passNum, 'height delta', delta, 'ms:', ms, 'fontsize:', fontSize);
        return;
      } 
      if (passNum >= 10) {
        // TODO: ht can be gt targetmax. when that's the case, shd revert to last good value lt targetmax
        clw('Max attempts reached, giving up');
        cl($child, 'height', height, 'targetmin', targetHeightMin, 'targetmax', targetHeightMax);
        return;
      }

      if (isTooShort) {
        // cl('is too short');
        fontSizeLowerBound = fontSize;
      } else { // isTooTall 
        // cl('is too tall');
        fontSizeUpperBound = fontSize;
      }
    }
  }  
}
